import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Input, Modal, Popconfirm, Select, Skeleton, Space, Steps, Tag } from 'antd';
import type { ExternalInvitationTargetMode } from '@youkuai/contracts';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './auth.tsx';
import { listCentralIdentities, listDynamicRoles } from './dynamic-access-api.ts';
import { cancelExternalInvitation, createExternalInvitation, listExternalInvitations,
  listExternalPersonCandidates, reconcileExternalInvitation,
  redeliverExternalInvitation, syncExternalIdentityMappings } from './external-invitations-api.ts';
import { Icon } from './icons.tsx';
import { ApiRequestError } from './project-api.ts';

const statusText: Record<string, string> = {
  PENDING_DELIVERY: '创建中', PENDING_ACCEPTANCE: '待接受', ACCEPTED: '待完成绑定',
  RECONCILED: '已完成', CANCELLED: '已取消', EXPIRED: '已过期', FAILED: '失败',
};

const identityPathText: Record<string, string> = {
  EXISTING_USER: '绑定现有统一用户', EXISTING_AD: '新建统一用户并认领现有 AD', NEW_USER: '全新用户',
};
const processingStatusText: Record<string, string> = {
  RESERVED: '准备提交', WAITING_IDENTITY: '等待接受', RETRY_PENDING: '等待重试', RECONCILING: '正在绑定',
  PROJECTING: '正在同步权限', COMPLETED: '已完成', FAILED: '处理失败', CANCELLED: '已结束',
};
const businessStatusText: Record<string, string> = {
  PENDING: '身份待接受', ACCEPTED: '身份已确认', CANCELLED: '邀请已取消', EXPIRED: '邀请已过期',
};

function failureText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const text: Record<string, string> = {
      INVITATION_CONFLICT: '该邮箱或历史人员已有进行中的邀请。',
      PERSON_VERSION_CONFLICT: '人员档案在邀请后发生了变化，请重新核对影响。',
      PERSON_ALREADY_BOUND: '这份人员档案已绑定统一用户。',
      PERSON_CLAIM_BLOCKED: '两份人员档案都已有业务历史，系统已阻止自动合并。',
      PERSON_IDENTITY_CONFLICT: '人员身份映射存在冲突，请先完成人工核验。',
      PERSON_IDENTITY_UNVERIFIED: '人员已有统一身份，但缺少已核验的 AD 稳定映射。',
      ROLE_NOT_ALLOWED: '外部执行人不能获得管理、财务或系统管理角色。',
      EXTERNAL_INVITATION_REQUEST_INVALID: '请检查邀请信息，邀请原因至少填写 3 个字符。',
      VALIDATION_ERROR: '邀请信息未通过身份中心校验，请检查后重试。',
      INVALID_EXPIRY: '账号到期日必须晚于当前时间。',
      INVALID_SPONSOR: '内部负责人必须是有效的内部员工。',
      IDENTITY_CONFLICT: '该邮箱在身份中心已有账号，请先核验是否为同一人。',
      IDENTITY_INVITATION_REJECTED: '统一身份中心拒绝了邀请请求，请检查填写内容。',
      IDEMPOTENCY_PAYLOAD_CONFLICT: '同一邀请请求不能更换人员或邀请内容。',
      CENTRAL_INVITATION_UNAVAILABLE: '统一身份中心暂时不可用，本地记录已保留错误信息。',
      IDENTITY_MAPPING_UNAVAILABLE: '可信身份映射暂时无法同步，请稍后重试。',
      INVITATION_NOT_PENDING: '这份邀请已接受、取消或失效，不能重新发送。',
      INVITATION_EXPIRED: '这份邀请已经过期，请重新发起邀请。',
      INVITATION_REDELIVERY_THROTTLED: '恢复邮件刚刚发送过，请至少等待 60 秒后再试。',
      FORBIDDEN: '只有系统管理员可以管理外部邀请。',
    };
    return text[error.code] ?? error.userMessage ?? `操作失败（${error.code}）`;
  }
  return error instanceof Error ? error.message : '操作失败';
}

/**
 * BUG-0150：第一步只在按钮上禁用、不说原因，业务看到的就是「不能发送邀请」。
 * 这里给三个必填项各自可复用的字段级提示；校验规则与 canNext 保持一致，不放松也不收紧。
 */
export function invitationEmailHint(value: string): string | null {
  const email = value.trim();
  if (!email) return null;
  return /.+@.+\..+/.test(email) ? null : '邮箱格式不正确，例如 name@qq.com';
}

export function invitationNameHint(value: string): string | null {
  const name = value.trim();
  if (!name) return null;
  return name.length >= 2 ? null : '姓名至少 2 个字符';
}

/**
 * 内部负责人提示分两种：没有可选负责人时给「先补内部用户」的说明（不是用户的错，
 * 不该首屏标红）；有候选但没选时才提示选择。
 */
export function invitationSponsorHint(sponsorId: string, sponsorCount = 1): string | null {
  if (sponsorCount === 0) return '当前没有可用的内部负责人，请先在身份中心补齐内部用户';
  return sponsorId ? null : '请选择内部负责人';
}

export function ExternalInvitationsPage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : '';
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [sponsorId, setSponsorId] = useState('');
  const [mode, setMode] = useState<ExternalInvitationTargetMode>('CLAIM_EXISTING');
  const [candidateQuery, setCandidateQuery] = useState('');
  const [personId, setPersonId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [accountExpiresAt, setAccountExpiresAt] = useState('');
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<{ displayName: string; inviteUrl: string;
    deliveryStatus: string | null; deliveryError: string | null } | null>(null);

  const invitations = useQuery({ queryKey: ['external-invitations', accountId],
    queryFn: () => listExternalInvitations(token), enabled: state.kind === 'ready' });
  const roles = useQuery({ queryKey: ['dynamic-roles', accountId], queryFn: () => listDynamicRoles(token),
    enabled: state.kind === 'ready' });
  const identities = useQuery({ queryKey: ['central-identities', accountId],
    queryFn: () => listCentralIdentities(token), enabled: state.kind === 'ready' });
  const candidates = useQuery({ queryKey: ['external-person-candidates', candidateQuery],
    queryFn: () => listExternalPersonCandidates(token, candidateQuery), enabled: open && step >= 1 && mode === 'CLAIM_EXISTING' });
  const selectedPerson = candidates.data?.items.find(item => item.personId === personId) ?? null;
  const externalRoles = useMemo(() => roles.data?.items.filter(role => role.status === 'ACTIVE' &&
    ['READ_ONLY','EXECUTOR','CONFIRMER','PLANNER'].includes(role.capabilityBaseline)) ?? [], [roles.data]);
  const sponsors = identities.data?.items.filter(identity => identity.kind === 'INTERNAL' &&
    identity.directoryStatus === 'ACTIVE') ?? [];

  const mutation = useMutation({ mutationFn: (action: () => Promise<unknown>) => action(),
    onError: error => setNotice({ type: 'error', text: failureText(error) }),
    onSuccess: async () => {
      setNotice(null);
      await client.invalidateQueries({ queryKey: ['external-invitations', accountId] });
    } });

  function start() {
    setStep(0); setEmail(''); setDisplayName(''); setSponsorId(sponsors[0]?.centralUserId ?? '');
    setMode('CLAIM_EXISTING'); setCandidateQuery(''); setPersonId('');
    setRoleId(externalRoles[0]?.roleId ?? ''); setAccountExpiresAt(''); setReason(''); setInviteUrl(null);
    setNotice(null); setOpen(true);
  }
  function close() { setOpen(false); setNotice(null); }
  const canNext = step === 0 ? /.+@.+\..+/.test(email) && displayName.trim().length >= 2 && !!sponsorId
    : step === 1 ? mode === 'CREATE_NEW' || Boolean(selectedPerson?.canClaim)
      : step === 2 ? Boolean(roleId && accountExpiresAt && reason.trim().length >= 3) : true;

  async function submit() {
    if (!canNext) return;
    const result = await mutation.mutateAsync(() => createExternalInvitation(token, {
      email, displayName: mode === 'CLAIM_EXISTING' && selectedPerson ? selectedPerson.displayName : displayName,
      sponsorCentralUserId: sponsorId, targetMode: mode,
      targetPersonId: mode === 'CLAIM_EXISTING' ? personId : null,
      expectedPersonVersion: mode === 'CLAIM_EXISTING' ? selectedPerson?.version : null,
      roleId, accountExpiresAt: new Date(`${accountExpiresAt}T23:59:59+08:00`).toISOString(), reason,
    })) as Awaited<ReturnType<typeof createExternalInvitation>>;
    setInviteUrl(result.inviteUrl); setStep(4);
  }

  async function redeliver(item: NonNullable<typeof invitations.data>['items'][number]) {
    const result = await mutation.mutateAsync(() => redeliverExternalInvitation(token, item.invitationId)) as
      Awaited<ReturnType<typeof redeliverExternalInvitation>>;
    setRecovery({ displayName: item.displayName, inviteUrl: result.inviteUrl,
      deliveryStatus: result.invitation.deliveryStatus, deliveryError: result.invitation.deliveryError });
  }

  async function syncMappings() {
    const result = await mutation.mutateAsync(() => syncExternalIdentityMappings(token)) as
      Awaited<ReturnType<typeof syncExternalIdentityMappings>>;
    await client.invalidateQueries({ queryKey: ['external-person-candidates'] });
    setNotice({ type: result.conflicts > 0 ? 'error' : 'success',
      text: `可信映射同步完成：匹配 ${result.matched}，新核验 ${result.verified}，未变化 ${result.unchanged}，冲突 ${result.conflicts}，本地未匹配 ${result.unmatched}。` });
  }

  return <main className="page-shell access-page external-invite-page">
    <header className="access-page-head"><div><span className="access-eyebrow">EXTERNAL ACCESS</span><h1>外部邀请</h1>
      <p>先建立可登录身份，再单独分配项目成员和职责；邀请不会自动让对方看到任何项目。</p></div>
      <Space><Button loading={mutation.isPending} onClick={() => void syncMappings()}>同步身份映射</Button>
        <Button type="primary" size="large" onClick={start}><Icon name="user" size={15} />邀请外部执行人</Button></Space></header>
    <nav className="access-tabs" aria-label="系统管理页面">
      <Link to="/system/accounts"><span>账号与访问</span><b>→</b></Link>
      <Link className="is-active" to="/system/external-invitations"><span>外部邀请</span><b>{invitations.data?.items.filter(i => i.businessStatus === 'PENDING').length ?? 0}</b></Link>
      <Link to="/system/roles"><span>角色与页面权限</span><b>→</b></Link>
    </nav>
    {notice && !open && !recovery && <Alert className="access-notice" showIcon type={notice.type} title={notice.text} closable onClose={() => setNotice(null)} />}
    <section className="external-invite-principles">
      <article><i>01</i><span><b>不按姓名自动合并</b><small>历史人员由管理员明确选择，保留原 person_id 和业务历史。</small></span></article>
      <article><i>02</i><span><b>登录权不等于项目权</b><small>接受邀请后仅可登录，项目成员需另行添加。</small></span></article>
      <article><i>03</i><span><b>个人结算不改口径</b><small>任务、费用和结算历史不搬迁、不重算。</small></span></article>
    </section>
    {invitations.isPending ? <Skeleton active paragraph={{ rows: 9 }} /> : <section className="external-invite-list">
      <header><div><span className="access-eyebrow">INVITATION LEDGER</span><h2>邀请台账</h2></div><small>一次性邀请链接 72 小时失效</small></header>
      <div>{invitations.data?.items.map(item => <article key={item.invitationId}>
        <div className="external-person-mark">{item.displayName.slice(0, 1)}</div>
        <div className="external-invite-main"><span><b>{item.displayName}</b><small>{item.email}</small></span>
          <p>{item.targetMode === 'CLAIM_EXISTING' ? `绑定 ${item.targetPersonName} · ${item.targetPersonCode}` : '接受后新建人员档案'} · {identityPathText[item.identityPath]} · {item.roleName}</p></div>
        <div className="external-invite-state"><Tag color={item.processingStatus === 'COMPLETED' ? 'green' : item.processingStatus === 'FAILED' ? 'red' : 'gold'}>{processingStatusText[item.processingStatus] ?? statusText[item.status]}</Tag>
          <small>{businessStatusText[item.businessStatus]} · {item.deliveryStatus ? `投递 ${item.deliveryStatus}` : '尚未投递'}</small></div>
        <div className="external-invite-date"><b>{new Date(item.createdAt).toLocaleDateString('zh-CN')}</b><small>账号至 {new Date(item.accountExpiresAt).toLocaleDateString('zh-CN')}</small></div>
        <div className="external-invite-actions">
          {['PENDING_ACCEPTANCE','ACCEPTED'].includes(item.status) && <Button size="small" loading={mutation.isPending}
            onClick={() => void mutation.mutateAsync(() => reconcileExternalInvitation(token, item.invitationId))}>立即刷新状态</Button>}
          {item.status === 'PENDING_ACCEPTANCE' && <Popconfirm title="重新发送并使旧的业务邀请链接失效？"
            description="不会延长原邀请的有效期；邮件链接仍然只能使用一次。"
            onConfirm={() => void redeliver(item)}><Button size="small" loading={mutation.isPending}>重新发送/恢复邀请</Button></Popconfirm>}
          {item.status === 'PENDING_ACCEPTANCE' && <Popconfirm title="取消这份邀请？" onConfirm={() => void mutation.mutateAsync(() => cancelExternalInvitation(token, item.invitationId))}>
            <Button size="small" danger>取消</Button></Popconfirm>}
        </div>{item.deliveryError && <p className="external-invite-error">{item.deliveryError}</p>}
      </article>)}{!invitations.data?.items.length && <Empty description="还没有外部邀请" />}</div>
    </section>}

    <Modal open={open} onCancel={close} width={880} title="邀请外部执行人" footer={step === 4 ?
      <Button type="primary" onClick={close}>完成</Button> : <><Button onClick={() => step ? setStep(step - 1) : close()}>{step ? '上一步' : '取消'}</Button>
      <Button type="primary" disabled={!canNext} loading={mutation.isPending} onClick={() => step === 3 ? void submit() : setStep(step + 1)}>{step === 3 ? '确认并发送邀请' : '下一步'}</Button></>}>
      {step < 4 && <Steps size="small" current={step} items={['基本信息','历史人员','账号与角色','影响确认'].map(title => ({ title }))} />}
      {notice && <Alert className="external-invite-modal-notice" showIcon type={notice.type} title={notice.text}
        closable onClose={() => setNotice(null)} />}
      <div className="external-invite-wizard">
        {step === 0 && <><h3>受邀人与内部负责人</h3><p>邀请邮箱将用于验证身份；内部负责人仅是身份中心的问责人。</p>
          <div className="external-form-grid"><label><span>姓名</span><Input value={displayName} onChange={e => setDisplayName(e.target.value)} />
            {invitationNameHint(displayName) && <small className="field-hint">{invitationNameHint(displayName)}</small>}</label>
          <label><span>邮箱</span><Input type="email" value={email} onChange={e => setEmail(e.target.value)} />
            {invitationEmailHint(email) && <small className="field-hint">{invitationEmailHint(email)}</small>}</label>
          <label className="wide"><span>内部负责人</span><Select value={sponsorId || null} onChange={setSponsorId} options={sponsors.map(i => ({ value: i.centralUserId, label: `${i.displayName}${i.departmentName ? ` · ${i.departmentName}` : ''}` }))} />
            {invitationSponsorHint(sponsorId, sponsors.length) && <small className={sponsors.length === 0 ? 'field-note' : 'field-hint'}>{invitationSponsorHint(sponsorId, sponsors.length)}</small>}</label></div></>}
        {step === 1 && <><h3>选择项目人员档案</h3><p>统一身份路径由服务端根据已核验映射自动确定；不会根据姓名或邮箱猜测，也不能在邀请表单中修改稳定字段。</p>
          <div className="external-mode-switch"><button className={mode === 'CLAIM_EXISTING' ? 'is-active' : ''} onClick={() => setMode('CLAIM_EXISTING')}>选择已有档案</button><button className={mode === 'CREATE_NEW' ? 'is-active' : ''} onClick={() => { setMode('CREATE_NEW'); setPersonId(''); }}>建立新档案</button></div>
          {mode === 'CLAIM_EXISTING' ? <><Input.Search allowClear placeholder="搜索姓名、人员编号、公司或邮箱" onSearch={setCandidateQuery} onChange={e => setCandidateQuery(e.target.value)} />
            <div className="external-candidates">{candidates.data?.items.map(item => <button key={item.personId} disabled={!item.canClaim} className={personId === item.personId ? 'is-selected' : ''} onClick={() => { setPersonId(item.personId); setDisplayName(item.displayName); }}>
              <span><b>{item.displayName}</b><small>{item.personCode} · {item.company ?? '未填公司'}</small></span><em>{identityPathText[item.identityPath ?? ''] ?? '需人工核验'} · {item.projectCount} 个项目 · {item.taskCount} 个任务{item.hasFinancialHistory ? ' · 有费用/结算历史' : ''}</em>{item.blockReason && <i>{item.blockReason}</i>}</button>)}</div></>
            : <Alert showIcon type="info" title="接受邀请后才会建立新人员档案，不会自动加入项目。" />}</>}
        {step === 2 && <><h3>登录角色与有效期</h3><p>第一阶段只允许执行、确认、策划或只读角色，不允许管理和财务角色。</p>
          <div className="external-form-grid"><label className="wide"><span>初始角色</span><Select value={roleId || null} onChange={setRoleId} options={externalRoles.map(r => ({ value: r.roleId, label: `${r.name} · ${r.capabilityBaseline}` }))} /></label>
          <label><span>账号到期日</span><Input type="date" min={new Date(Date.now() + 86400000).toISOString().slice(0,10)} value={accountExpiresAt} onChange={e => setAccountExpiresAt(e.target.value)} /></label>
          <label><span>邀请原因（至少 3 个字符）</span><Input value={reason} onChange={e => setReason(e.target.value)} placeholder="例如：参与内容制作" /></label></div></>}
        {step === 3 && <><h3>最后确认影响范围</h3><div className="external-impact">
          <article><b>本次邀请</b><p>{displayName} · {email}<br />负责人：{sponsors.find(i => i.centralUserId === sponsorId)?.displayName ?? sponsorId}<br />合作至：{accountExpiresAt} · 原因：{reason}</p></article>
          <article><b>会发生</b><p>创建一份 72 小时有效的邮件邀请；身份路径为“{mode === 'CLAIM_EXISTING' ? identityPathText[selectedPerson?.identityPath ?? ''] : identityPathText.NEW_USER}”，接受后{mode === 'CLAIM_EXISTING' ? `绑定到 ${selectedPerson?.displayName} 的项目档案` : '建立新人员档案'}。</p></article>
          {mode === 'CLAIM_EXISTING' && selectedPerson && selectedPerson.identityPath !== 'NEW_USER' && <article><b>已核验身份（只读）</b><p>{selectedPerson.identityPath === 'EXISTING_USER' && <>统一用户：{selectedPerson.centralUserId}<br /></>}AD：{selectedPerson.adUsername} · UID {selectedPerson.adUidNumber}</p></article>}
          <article><b>不会发生</b><p>不会修改现有 Keycloak 或项目盘密码，不恢复历史项目权限，不转移或重算费用与结算，也不会降低老用户已有角色。</p></article></div></>}
        {step === 4 && <div className="external-invite-success"><i><Icon name="check" size={22} /></i><h3>邀请已创建</h3><p>邮件投递状态已记录。系统会在对方接受后自动同步，也可以在台账点击“立即刷新状态”。</p>
          {inviteUrl && <div><code>{inviteUrl}</code><Button onClick={() => void navigator.clipboard.writeText(inviteUrl)}>复制一次性链接</Button><small>仅本次显示；只在邮件未配置或投递失败时通过安全渠道转交。</small></div>}</div>}
      </div>
    </Modal>
    <Modal open={Boolean(recovery)} onCancel={() => setRecovery(null)} title="恢复邀请已生成"
      footer={<Button type="primary" onClick={() => setRecovery(null)}>完成</Button>}>
      {recovery && <div className="external-invite-success"><i><Icon name="check" size={22} /></i>
        <h3>{recovery.displayName} 的恢复邀请已处理</h3>
        <p>投递状态：{recovery.deliveryStatus ?? '未知'}。原业务邀请链接已经失效，邀请截止时间没有延长。</p>
        {recovery.deliveryError && <Alert showIcon type="warning" title="邮件投递未完成"
          description={recovery.deliveryError} />}
        <div><code>{recovery.inviteUrl}</code><Button onClick={() => void navigator.clipboard.writeText(recovery.inviteUrl)}>复制恢复链接</Button>
          <small>邮件未收到时，可通过安全渠道交给受邀人。该链接仍然只能用于这一份邀请。</small></div>
      </div>}
    </Modal>
  </main>;
}
